import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { GridComponent, GridComponentProps } from './Grid';

export { GridComponent };

const VERTICAL_PADDING = 16;
const FALLBACK_ROW_HEIGHT = 56;

export interface VirtualizedGridListProps<T> {
  items: T[];
  columns: string;
  columnGap?: string;
  visibleRowCount?: number;
  resetScrollKey?: unknown;
  getRowKey: (item: T) => string;
  renderRow: (item: T) => React.ReactNode;
  gridProps?: Partial<GridComponentProps>;
  footer?: React.ReactNode;
}

export const VirtualizedGridList = <T,>({
  items,
  columns,
  columnGap,
  visibleRowCount = 10,
  resetScrollKey,
  getRowKey,
  renderRow,
  gridProps,
  footer,
}: VirtualizedGridListProps<T>): JSX.Element => {
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const gridElementRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState<number | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => rowHeight ?? FALLBACK_ROW_HEIGHT,
    overscan: 8,
  });

  // Only jump to top when the caller signals the result set genuinely changed
  // (e.g. a new search/filter), not on every re-render of `items` caused by a row update.
  useEffect(() => {
    if (scrollElementRef.current) scrollElementRef.current.scrollTop = 0;
  }, [resetScrollKey]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const paddingTop = (virtualItems[0]?.start ?? 0) + VERTICAL_PADDING;
  const paddingBottom = totalSize - (lastVirtualItem?.end ?? 0) + VERTICAL_PADDING;

  useLayoutEffect(() => {
    const gridEl = gridElementRef.current;
    if (!gridEl || virtualItems.length === 0) return;

    const contentHeight = gridEl.getBoundingClientRect().height - paddingTop - paddingBottom;
    const measured = contentHeight / virtualItems.length;
    if (measured > 0 && Math.abs(measured - (rowHeight ?? 0)) > 0.1) setRowHeight(measured);
  }, [items, virtualItems.length, paddingTop, paddingBottom, rowHeight]);

  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight]);

  const visibleRows = Math.min(items.length, visibleRowCount);

  // Only the top padding applies here: this bounds the viewport to exactly `visibleRows` rows
  // starting from the top, with no trailing bottom padding (that only exists after the real last row).
  const maxHeight =
    items.length > visibleRowCount ? visibleRows * (rowHeight ?? FALLBACK_ROW_HEIGHT) + VERTICAL_PADDING : undefined;

  return (
    <div ref={scrollElementRef} style={{ maxHeight, overflow: maxHeight ? 'auto' : 'visible' }}>
      <GridComponent
        ref={gridElementRef}
        columns={columns}
        columnGap={columnGap}
        style={{ paddingTop, paddingBottom }}
        {...gridProps}
      >
        {virtualItems.map((virtualRow) => {
          const item = items[virtualRow.index];
          if (!item) return null;
          return <React.Fragment key={getRowKey(item)}>{renderRow(item)}</React.Fragment>;
        })}
      </GridComponent>
      {footer}
    </div>
  );
};
